// src/config/firebase-config.js
import { logger } from "../utils/logger";

// --- Firebase v10+ (modular) ---
import { initializeApp } from "firebase/app";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getFunctions } from "firebase/functions";
import { getAnalytics } from "firebase/analytics";

// --- Firebase Compat (legacy support) ---
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/functions";

// --- Project config (same as before) ---
const firebaseConfig = {
  apiKey: "AIzaSyCo0ijySvDyrnHiu6TY5W-i2AkjtYyCY-c",
  authDomain: "sbe-40.firebaseapp.com",
  projectId: "sbe-40",
  storageBucket: "sbe-40.firebasestorage.app",
  messagingSenderId: "472818470106",
  appId: "1:472818470106:web:bfc161ffd7448ee3e4a9e3",
  measurementId: "G-0J38746030",
};

// --- Env flags (for logging/analytics gating only) ---
const isDev = process.env.NODE_ENV === 'development';
const isProd = process.env.NODE_ENV === 'production';

logger.debug(` Firebase initializing... Environment: ${isDev ? "development" : "production"}`);

// --- Initialize v10+ app ---
let app;
try {
  app = initializeApp(firebaseConfig);
  logger.success(" Firebase v10+ app initialized");
} catch (error) {
  console.error("❌ Firebase app initialization failed:", error);
  throw error;
}

// --- v10+ services (exported) ---
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);

// --- Initialize Compat SDK (for any legacy code paths) ---
let compatDb, compatAuth, compatFunctions;
try {
  if (!firebase.apps.length) {
    firebase.initializeApp(firebaseConfig);
  }
  compatDb = firebase.firestore();
  compatAuth = firebase.auth();
  compatFunctions = firebase.functions();
  logger.success(" Firebase compat SDK initialized");
} catch (error) {
  console.error("❌ Firebase compat initialization failed:", error);
  throw error;
}

// --- Analytics (prod only, browser only) ---
export let analytics = null;
if (isProd && typeof window !== "undefined") {
  try {
    analytics = getAnalytics(app);
    logger.success(" Analytics initialized");
  } catch (error) {
    console.warn("⚠️ Analytics failed:", error?.message);
  }
}

// --- Global exposure (optional, matches your previous pattern) ---
if (typeof window !== "undefined") {
  // Compat
  window.firebase = firebase;
  window.db = compatDb;
  window.auth = compatAuth;
  window.functions = compatFunctions;

  // Modular helpers
  window.modernFirebase = {
    app,
    auth,
    db,
    functions,
    collection: (name) => collection(db, name),
    getDocs: getDocs,
  };

  window.FIREBASE_READY = true;
  logger.success(" Firebase services ready (both compat and v10+)");
}

// --- Connection test utility (optional) ---
export const testFirebaseConnection = async () => {
  try {
    logger.debug("🧪 Testing Firebase connections...");

    // Compat
    const compatTest = await compatDb.collection("health").limit(1).get();
    logger.success(" Compat Firestore working! Docs:", compatTest.size);

    // v10+
    const modernTest = await getDocs(collection(db, "health"));
    logger.success(" Modern Firestore working! Docs:", modernTest.size);

    return true;
  } catch (error) {
    // In production, this can fail due to restrictive rules. Don't scare users.
    if (isDev) {
      console.warn("⚠️ Firebase connection test failed:", error);
    }
    return false;
  }
};

// Auto-run a quick test in browser
if (typeof window !== "undefined" && isDev) {
  setTimeout(() => {
    testFirebaseConnection();
  }, 800);
}

export default app;
